"use strict";
/**
 * Mandatory form-field requirements, parametrised along four dimensions:
 *   • section — the project edit section the fields belong to (`section`)
 *   • progress — the lifecycle gate, if a section ever varies by step (`step`)
 *   • project type — VERANDA / PERGOLA / POOL (optional matcher)
 *   • account type — ARCHITECT / SALESMAN / … (optional matcher)
 *
 * This is the form-field sibling of `files/requirements.ts`. A rule with no
 * `step` / `projectType` / `accountType` applies to every value of that
 * dimension. When several rules match a request their `fields` lists are
 * concatenated in declaration order, so a more specific rule listed later
 * appends its requirements.
 *
 * Single source of truth: the backend section-status checkers turn these
 * descriptors into validation `errors` (which block section validation) and the
 * frontend forms turn them into AntD `required` markers (warning-only, so saving
 * is never blocked). Server enforcement and the form UI therefore can't drift.
 *
 * Matcher values are plain strings (`ProjectStep.*`, `ProjectType.*`,
 * `AccountType.*`) to keep this module decoupled from the other shared enums.
 *
 * Numeric semantics mirror the backend `greater(data, path, min)` helper: a
 * value passes when `value >= min` (so `min` is the inclusive floor, not a
 * strict bound). Keep `min` here in sync with the validation threshold, which is
 * distinct from a number input's display `min` in `form-fields.ts`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.requiredFieldsFor = exports.resolveRequiredFields = exports.FIELD_REQUIREMENT_RULES = void 0;
/**
 * Canonical rules — exact parity with the legacy hardcoded backend checkers in
 * `backend/src/features/project-management/form-status/<section>.ts`. The
 * descriptor order matches the order those checkers emit errors, and the `name`
 * fields preserve the historic `field_name` strings verbatim (note the space vs
 * underscore difference between pergola `*_count` and veranda `* count`).
 */
exports.FIELD_REQUIREMENT_RULES = [
    {
        section: 'EXISTING_BUILDING',
        fields: [
            { kind: 'notNull', field: 'allotment_min10' },
            { kind: 'notNull', field: 'primary_residence' },
            { kind: 'notNull', field: 'secondary_residence' },
            { kind: 'min', field: 'rdc_area', min: 1 },
        ],
    },
    {
        section: 'POOL',
        fields: [
            { kind: 'notNull', field: 'area' },
            { kind: 'min', field: 'height_min', min: 0.01 },
            { kind: 'min', field: 'height_max', min: 0.01 },
            { kind: 'min', field: 'width', min: 0.01 },
            { kind: 'min', field: 'length', min: 0.01 },
            { kind: 'neq', field: 'status', value: 'REFUSED' },
            { kind: 'notNull', field: 'has_liner' },
            {
                kind: 'when',
                whenField: 'has_liner',
                then: {
                    kind: 'count',
                    field: 'liner_colors',
                    countPath: 'liner_colors_aggregate.aggregate.count',
                    min: 1,
                    name: 'liner_colors count',
                },
            },
            { kind: 'notNull', field: 'has_margel' },
            {
                kind: 'when',
                whenField: 'has_margel',
                then: {
                    kind: 'count',
                    field: 'margel_colors',
                    countPath: 'margel_colors_aggregate.aggregate.count',
                    min: 1,
                    name: 'margel_colors count',
                },
            },
            { kind: 'notNull', field: 'has_cover' },
            {
                kind: 'when',
                whenField: 'has_cover',
                then: {
                    kind: 'count',
                    field: 'cover_colors',
                    countPath: 'cover_colors_aggregate.aggregate.count',
                    min: 1,
                    name: 'cover_colors count',
                },
            },
            {
                kind: 'when',
                whenField: 'has_cover',
                then: {
                    kind: 'count',
                    field: 'cover_types',
                    countPath: 'cover_types_aggregate.aggregate.count',
                    min: 1,
                    name: 'cover_types count',
                },
            },
        ],
    },
    {
        section: 'PERGOLA',
        fields: [
            { kind: 'notNull', field: 'area' },
            { kind: 'min', field: 'height_below_hook', min: 1 },
            { kind: 'min', field: 'height_below_sewer', min: 1 },
            { kind: 'min', field: 'length', min: 0.01 },
            { kind: 'min', field: 'width', min: 0.01 },
            { kind: 'neq', field: 'status', value: 'REFUSED' },
            {
                kind: 'count',
                field: 'roof_materials',
                countPath: 'roof_materials_aggregate.aggregate.count',
                min: 1,
                name: 'roof_materials_count',
            },
            {
                kind: 'count',
                field: 'pergola_colors',
                countPath: 'pergola_colors_aggregate.aggregate.count',
                min: 1,
                name: 'pergola_colors_count',
            },
            {
                kind: 'count',
                field: 'pergola_materials',
                countPath: 'pergola_materials_aggregate.aggregate.count',
                min: 1,
                name: 'pergola_materials_count',
            },
            // `base_panel_materials` (Matériaux du soubassement) is intentionally
            // optional for every project type — see issue #476.
        ],
    },
    {
        section: 'VERANDA',
        fields: [
            { kind: 'notNull', field: 'area' },
            { kind: 'min', field: 'height_below_hook', min: 1 },
            { kind: 'min', field: 'height_below_sewer', min: 1 },
            { kind: 'min', field: 'length', min: 0.01 },
            { kind: 'min', field: 'width', min: 0.01 },
            { kind: 'neq', field: 'status', value: 'REFUSED' },
            {
                kind: 'count',
                field: 'roof_materials',
                countPath: 'roof_materials_aggregate.aggregate.count',
                min: 1,
                name: 'roof_materials count',
            },
            {
                kind: 'count',
                field: 'veranda_colors',
                countPath: 'veranda_colors_aggregate.aggregate.count',
                min: 1,
                name: 'veranda_colors count',
            },
            {
                kind: 'count',
                field: 'veranda_materials',
                countPath: 'veranda_materials_aggregate.aggregate.count',
                min: 1,
                name: 'veranda_materials count',
            },
            // `base_panel_materials` (Matériaux du soubassement) is intentionally
            // optional for every project type — see issue #476.
        ],
    },
    {
        section: 'LAND',
        fields: [
            { kind: 'notEmpty', field: 'city' },
            { kind: 'notEmpty', field: 'postal_code' },
            { kind: 'notEmpty', field: 'street_name' },
            { kind: 'notEmpty', field: 'street_number' },
        ],
    },
];
const ruleMatches = (rule, ctx) => {
    if (rule.section !== ctx.section)
        return false;
    if (rule.step !== undefined && rule.step !== ctx.step)
        return false;
    if (rule.projectType !== undefined && rule.projectType !== ctx.projectType)
        return false;
    if (rule.accountType !== undefined && rule.accountType !== ctx.accountType)
        return false;
    return true;
};
/** Pure resolver — concatenates every matching rule's `fields` in order. */
const resolveRequiredFields = (rules, ctx) => {
    const result = [];
    for (const rule of rules) {
        if (!ruleMatches(rule, ctx))
            continue;
        result.push(...rule.fields);
    }
    return result;
};
exports.resolveRequiredFields = resolveRequiredFields;
/** The required form fields for a `(section, step?, projectType?, accountType?)` request. */
const requiredFieldsFor = (ctx) => (0, exports.resolveRequiredFields)(exports.FIELD_REQUIREMENT_RULES, ctx);
exports.requiredFieldsFor = requiredFieldsFor;
//# sourceMappingURL=field-requirements.js.map